import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, FilePlus, X, ArrowLeft, CheckCircle, MapPin, DollarSign } from 'lucide-react';
import api from '../../api/api'; // Ensure you have your axios wrapper
import showToast from '../ShowToast'; // Ensure path is correct
import PostcodeAutocomplete from '../PostcodeAutocomplete/PostcodeAutocomplete'; // Ensure path is correct
import './CreateProjectModal.css'; 
import LocationInputGroup from '../Form/LocationInputGroup';

const CreateProjectModal = ({ isOpen, onClose }) => {
    const navigate = useNavigate();
    const [view, setView] = useState('selection'); // 'selection' or 'form'
    const [isLoading, setIsLoading] = useState(false);

    // Form State
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        budget: '',
        postcode: '',
        suburb: '',
        state: '', 
        locationDisplay: '' 
    });

    if (!isOpen) return null;

    // --- HANDLERS ---

    const handleAIProjectClick = () => {
        navigate('/ai-project-onboarding/select-template');
        onClose();
    };

    const handleBlankProjectClick = () => {
        setView('form'); // Switch to form view inside modal
    };

    const handleLocationSelect = (loc) => {
        setFormData(prev => ({
            ...prev,
            postcode: loc.postcode || '',
            suburb: loc.suburb || '',
            state: loc.state || '',
            locationDisplay: loc.fullAddress // <--- Store the pretty string
        }));
    };

    const handleCreate = async () => {
        if (!formData.name || formData.name.length < 3) {
            showToast('error', 'Please enter a valid project name.');
            return;
        }

        setIsLoading(true);
        try {
            // 1. Get User Context
            const rawUserType = localStorage.getItem('userType') || 'HomeUser';
            let userId = 0;
            let apiUserType = "HomeUser";

            if (rawUserType === "Home" || rawUserType === "HomeUser") {
                userId = parseInt(localStorage.getItem('loggedInHomeUserId') || '0', 10);
            } else {
                apiUserType = "BusinessUser";
                userId = parseInt(localStorage.getItem('loggedInBusinessUserId') || '0', 10);
            }

            // 2. Build Payload
            const payload = {
                Name: formData.name,
                Description: formData.description,
                Postcode: formData.postcode,
                Suburb: formData.suburb,
                MyState: formData.state,
                Budget: formData.budget ? parseFloat(formData.budget) : null,
                Status: 'Planning',
                UserType: apiUserType,
                OwnerUserId: userId
            };

            // 3. API Call
            const response = await api.post('/api/UserProjects', payload); // Adjust endpoint if yours is different
            
            showToast('success', 'Project created successfully!');
            
            // 4. Navigate to the new Project Detail page
            const newProjectId = response.data.projectId || response.data.ProjectId || response.data.id;
            navigate(`/project/${newProjectId}`);
            onClose();

        } catch (error) {
            console.error(error);
            showToast('error', 'Failed to create project.');
        } finally {
            setIsLoading(false);
        }
    };

    // --- RENDER ---

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
                <button className="modal-close-btn" onClick={onClose}>
                    <X size={24} />
                </button>

                {/* VIEW 1: SELECTION */}
                {view === 'selection' && (
                    <>
                        <div className="modal-header-center">
                            <h2>Create a New Project</h2>
                            <p>How would you like to start?</p>
                        </div>

                        <div className="project-options">
                            <div className="option-card" onClick={handleAIProjectClick}>
                                <div className="option-icon">
                                    <Sparkles size={32} color="#007bff" />
                                </div>
                                <h3>AI-Assisted Project</h3>
                                <p><strong>Recommended</strong></p>
                                <p>Let our AI build a project plan for you. We'll suggest phases, jobs, and contractors.</p>
                                <button className="primary-btn">Start AI Project</button>
                            </div>

                            <div className="option-card" onClick={handleBlankProjectClick}>
                                <div className="option-icon">
                                    <FilePlus size={32} color="#6c757d" />
                                </div>
                                <h3>Blank Project</h3>
                                <p>Start with an empty project and add jobs manually.</p>
                                <p>Best for custom or unique projects where you know exactly what you need.</p>
                                <button className="secondary-btn">Start Blank Project</button>
                            </div>
                        </div>
                    </>
                )}

                {/* VIEW 2: BLANK PROJECT FORM */}
                {view === 'form' && (
                    <>
                        <div className="modal-header-left">
                            <button className="back-link-btn" onClick={() => setView('selection')}>
                                <ArrowLeft size={18} /> Back
                            </button>
                            <h2>Project Details</h2>
                        </div>

                        <div className="form-container">
                            <div className="form-group">
                                <label>Project Name <span className="required">*</span></label>
                                <input 
                                    type="text" 
                                    placeholder="e.g. Backyard Landscaping" 
                                    value={formData.name}
                                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                                />
                            </div>

                            <div className="form-group">
                                <label>Description</label>
                                <textarea 
                                    rows="3"
                                    placeholder="Briefly describe what you want to achieve..."
                                    value={formData.description}
                                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                                />
                            </div>

                            <div className="form-row">
                                <div className="form-group half">

                                <LocationInputGroup 
                                    label="Location"
                                    value={formData.locationDisplay} // <--- Pass the display string, not just postcode
                                    onLocationSelect={handleLocationSelect}
                                    suburb={formData.suburb}
                                    state={formData.state}
                                />                                  

                                </div>

                                <div className="form-group half">
                                    <label>Estimated Budget</label>
                                    <div className="input-with-icon">
                                        <DollarSign size={16} className="input-icon"/>
                                        <input 
                                            type="number" 
                                            placeholder="0.00" 
                                            value={formData.budget}
                                            onChange={(e) => setFormData({...formData, budget: e.target.value})}
                                        />
                                    </div>
                                </div>
                            </div>

                            <button 
                                className="primary-btn full-width" 
                                onClick={handleCreate}
                                disabled={isLoading}
                            >
                                {isLoading ? 'Creating...' : 'Create Project'}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default CreateProjectModal;